import { Link, type LinkProps } from '@tanstack/react-router';
import { ChevronLeft, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { SocialNetworkId } from '../networks';

import { NetworkTile } from './network-tile';

const ITEM =
  'text-muted-foreground hover:text-foreground hover:bg-accent data-[status=active]:bg-primary-tint data-[status=active]:text-primary-tint-foreground flex shrink-0 items-center gap-2 rounded-md px-2.5 py-1.5 text-sm whitespace-nowrap transition-colors data-[status=active]:font-medium';

export interface NetworkNavItem {
  key: string;
  label: string;
  icon: LucideIcon;
  link: LinkProps;
  /** Highlight only on this exact route, not on the routes below it. */
  exact?: boolean;
  tour?: string;
}

export interface NetworkNavGroup {
  key: string;
  label: string;
  items: NetworkNavItem[];
}

export interface NetworkLayoutProps {
  storeId: string;
  network: SocialNetworkId;
  name: string;
  /** Under the name: the connected account, or a status. */
  subtitle: ReactNode;
  badge?: ReactNode;
  groups: NetworkNavGroup[];
  children: ReactNode;
}

/**
 * One network's section: a secondary sidebar with the network's pages beside the page itself.
 * Below `lg` the sidebar folds into a scrolling row of links above the page.
 */
export function NetworkLayout({
  storeId,
  network,
  name,
  subtitle,
  badge,
  groups,
  children,
}: NetworkLayoutProps) {
  const { t } = useTranslation('social');
  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      <aside className="bg-sidebar/40 border-b lg:sticky lg:top-14 lg:h-[calc(100svh-3.5rem)] lg:w-60 lg:shrink-0 lg:overflow-y-auto lg:border-r lg:border-b-0">
        <div className="flex flex-col gap-4 px-4 pt-4 lg:px-3 lg:py-6">
          <Link
            to="/s/$storeId/social"
            params={{ storeId }}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-xs font-medium"
          >
            <ChevronLeft className="size-3.5" aria-hidden />
            {t('nav.allNetworks')}
          </Link>
          <div className="flex items-center gap-2.5 lg:px-1">
            <NetworkTile network={network} size="sm" />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="flex flex-wrap items-center gap-1.5 text-sm font-semibold">
                {name}
                {badge}
              </span>
              <span className="text-muted-foreground truncate text-xs">{subtitle}</span>
            </div>
          </div>
          <nav
            aria-label={t('nav.label', { network: name })}
            className="-mx-4 flex gap-1 overflow-x-auto px-4 pb-3 lg:mx-0 lg:flex-col lg:gap-5 lg:overflow-visible lg:px-0 lg:pb-0"
          >
            {groups.map((group) => (
              <div key={group.key} className="contents lg:flex lg:flex-col lg:gap-0.5">
                <h2 className="text-muted-foreground hidden px-2.5 pb-1 text-[11px] font-semibold tracking-wide uppercase lg:block">
                  {group.label}
                </h2>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      {...item.link}
                      activeOptions={{ exact: item.exact ?? false, includeSearch: false }}
                      className={ITEM}
                      data-tour={item.tour}
                    >
                      <Icon className="size-4 shrink-0" aria-hidden />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>
        </div>
      </aside>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
